/** Chamber (tenant) contracts — spec §5. */
import { z } from 'zod';
import { auAddressSchema, emailSchema, phoneSchema, slugSchema } from './primitives.js';
const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a 6-digit hex colour, e.g. #1F4D36');
export const createChamberSchema = z.object({
    slug: slugSchema,
    legalName: z.string().trim().min(2).max(200),
    /** What appears in the console header, where the legal name would not fit. */
    shortName: z.string().trim().min(2).max(60),
    region: z.string().trim().max(60).optional().nullable(),
    contactEmail: emailSchema,
    contactPhone: phoneSchema.optional().nullable(),
    address: auAddressSchema.optional().nullable(),
    websiteUrl: z.string().url().max(500).optional().nullable(),
    /**
     * Resolved into CSS custom properties at app load, so a chamber's console carries its
     * own brand without a separate build (D-008). Contrast is validated at apply time.
     */
    primaryColor: hexColorSchema.default('#1F4D36'),
    secondaryColor: hexColorSchema.default('#C9DEA6'),
    logoKey: z.string().max(500).optional().nullable(),
    /** Certificates issued under a sandbox chamber are watermarked and excluded from the
     *  real number sequence (spec v3 §21). */
    sandbox: z.boolean().default(false),
    documentTypeCodes: z.array(z.string()).default([]),
});
export const updateChamberSchema = createChamberSchema.partial().omit({ slug: true });
/** Brand colours, changed after creation from the chamber's page in the platform console. */
export const updateChamberBrandingSchema = z
    .object({ primaryColor: hexColorSchema, secondaryColor: hexColorSchema })
    .partial()
    .refine((v) => v.primaryColor !== undefined || v.secondaryColor !== undefined, {
    message: 'Nothing to change',
});
/**
 * A chamber logo. PNG or JPEG only: it is embedded in emails, where SVG does not render
 * reliably, and an uploaded SVG can carry script.
 */
export const uploadChamberLogoSchema = z.object({
    filename: z.string().trim().min(1).max(255),
    contentBase64: z.string().min(1),
});
export const chamberBrandingSchema = z.object({
    id: z.string(),
    slug: z.string(),
    legalName: z.string(),
    shortName: z.string(),
    logoUrl: z.string().nullable(),
    primaryColor: z.string(),
    secondaryColor: z.string(),
    contactEmail: z.string(),
    websiteUrl: z.string().nullable(),
});
/**
 * Go-live gate. A chamber that can receive requests but cannot stamp them produces a
 * queue nobody can clear, so the readiness checklist blocks activation rather than
 * letting the problem surface at the first approval.
 */
export const chamberReadinessSchema = z.object({
    ready: z.boolean(),
    /** Where exporters go. Shown beside the checklist so nobody has to guess the address. */
    links: z.object({ submitUrl: z.string(), registerUrl: z.string() }),
    checks: z.array(z.object({
        key: z.string(),
        label: z.string(),
        passed: z.boolean(),
        detail: z.string().nullable(),
        /** Where to go and fix it. */
        remedyPath: z.string().nullable(),
    })),
});
//# sourceMappingURL=chamber.js.map